import { trackUser } from '@api/tracking/client';
import { useRagnar } from '@mentimeter/ragnar-react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { IdentitiesList } from '@mentimeter/slides-presentation-ui/identified-responses/list';
import { IdentitiesListModal } from '@mentimeter/slides-presentation-ui/identified-responses/modal';
import { Bars } from '@mentimeter/visualizations-react';
import { FullSizeContainer } from '@mentimeter/visualizations-react/utils/full-size-container';
import { useCallback } from 'react';
import type {
  Choice,
  ChoicesVisualizationProps,
  OpenedIdentifiedResponsesListEvent,
} from '../types';
import {
  getResizedImageUrlByWidth,
  preloadChoicesImages,
} from '../utils/barsHelper';
import {
  getIdentitiesWithNameFallback,
  getParticipantsWithNameFallback,
} from '../utils/getIdentitiesWithNameFallback';
import { useIdentifiedResultsListState } from './useIdentifiedResultsListState';

// keeping the same props as the current visualizations for the simplicity
export const BarsDataHandler = ({
  showCorrect,
  results,
  identifiedResults,
  shouldShowPercent,
  choices,
  shouldHideResults,
  shouldAnimate,
  renderChoiceVisualization,
  renderChoiceLabel,
}: ChoicesVisualizationProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
      fonts: { body },
    },
  } = useRagnar();

  const { results: currentResults, respondents } = results;

  const visualizationData = currentResults.map((result) => ({
    id: result.id,
    score: result.score.reduce((acc, score) => acc + score, 0),
  }));

  const hasImages = choices.some((c) => c.imageUrl);
  const handleGetImages = useCallback(
    ({ width }: { width: number }): Record<string, string> => {
      const imagesMap: Record<string, string> = {};
      choices.forEach(async (choice) => {
        const imageUrl = choice.imageUrl;
        if (imageUrl && hasImages) {
          await new Promise((resolve) => {
            const img = new Image();
            img.src = getResizedImageUrlByWidth(imageUrl, width * 2) ?? '';
            preloadChoicesImages(img.src);
            imagesMap[choice.id] = img.src;
            resolve(img);
          });
        }
      });
      return imagesMap;
    },
    [choices, hasImages],
  );

  const handleShowIdentitiesList = (choiceId: Choice['id']) => {
    trackUser({
      event: 'Opened identified responses list',
      properties: {
        'slide type': 'choices',
        visualization: 'choices',
        placement: 'Visualization',
        context: 'Presentation view',
      },
    } satisfies OpenedIdentifiedResponsesListEvent);
    setIdentitiesListChoiceId(choiceId);
  };

  const {
    setIdentitiesListChoiceId,
    identityListChoice,
    identityListChoiceIndex,
    activeChoiceIdentities,
  } = useIdentifiedResultsListState({
    choices,
    identifiedResults: identifiedResults ?? [],
    shouldHideResults,
  });

  const shouldShowIdentitiesList = Boolean(
    identityListChoice !== undefined &&
      activeChoiceIdentities?.participantIdentities?.length &&
      activeChoiceIdentities.participantIdentities.length > 0 &&
      visualizationData.length > 0,
  );

  return (
    <Box px="space2" width="100%" height="100%">
      <FullSizeContainer>
        {(width, height) => (
          <Bars
            colors={fillColors as [string, ...[string]]}
            textColor={textColor}
            data={visualizationData}
            staticOptions={choices}
            onGetImages={hasImages ? handleGetImages : undefined}
            width={width}
            height={height}
            shouldShowScorePercentage={
              shouldShowPercent ? { percentOf: respondents } : false
            }
            shouldShowCorrect={showCorrect}
            isScoreHidden={shouldHideResults}
            disableAnimations={!shouldAnimate}
            identities={getIdentitiesWithNameFallback(identifiedResults)}
            onClickOption={
              identifiedResults === null ? undefined : handleShowIdentitiesList
            }
            fontFamily={body}
            renderChoiceVisualization={renderChoiceVisualization}
            renderChoiceLabel={renderChoiceLabel}
          />
        )}
      </FullSizeContainer>
      {shouldShowIdentitiesList && (
        <IdentitiesListModal onDismiss={() => setIdentitiesListChoiceId(null)}>
          <IdentitiesList
            identities={
              getParticipantsWithNameFallback(
                activeChoiceIdentities?.participantIdentities,
              ) ?? []
            }
            color={
              fillColors[identityListChoiceIndex % fillColors.length] ?? '#000'
            }
            title={identityListChoice?.label ?? 'Choice not found'}
            totalResponses={activeChoiceIdentities?.responseCount ?? 0}
          />
        </IdentitiesListModal>
      )}
    </Box>
  );
};
